import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import TransportNavbar from "../../components/TransportNavbar";

const API = process.env.REACT_APP_API_URL;

const Section = ({ title, children }) => (
  <section style={{ marginBottom: 32 }}>
    <h2 style={{ margin: "16px 0" }}>{title}</h2>
    {children}
  </section>
);

const List = ({
  items,
  onApprove,
  onReject,
  onViewPhoto,
  pageSize = 10,
}) => {
  const [page, setPage] = useState(1);

  if (!items || items.length === 0) {
    return <p style={{ margin: 0 }}>Geen items.</p>;
  }

  const totalPages = Math.ceil(items.length / pageSize);

  const paginatedItems = items.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  return (
    <>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={th}>Datum gemeld</th>
              <th style={th}>Klant (Companyid)</th>
              <th style={th}>Ship-to code</th>
              <th style={th}>Gemeld door</th>
              <th style={th}>Fust</th>
              <th style={th}>Foto</th>
              <th style={th}>Status</th>
              <th style={th}>Acties</th>
            </tr>
          </thead>

          <tbody>
            {paginatedItems.map((row) => (
              <tr key={row._id}>
                <td style={td}>
                  {formatDateTime(row.dateReported || row.createdAt)}
                </td>

                <td style={td}>
                  <strong>{row.companyName || "Onbekend"}</strong>
                  <br />
                  <small>{row.companyId}</small>
                </td>

                <td style={td}>
                  <strong>{row.shippingName || "n.v.t."}</strong>
                  <br />
                  <small>{row.shippingCode || "n.v.t."}</small>
                </td>

                <td style={td}>{row.reportedBy || "-"}</td>

                <td style={{ ...td, whiteSpace: "nowrap" }}>
                  {row.items?.map((it, idx) => (
                    <span
                      key={idx}
                      style={{ display: "block", marginRight: 8 }}
                    >
                      <strong>{it.no}</strong>: {it.amount}
                    </span>
                  ))}
                </td>

                <td style={td}>
                  {row.photo?.url ? (
                    <button
                      type="button"
                      onClick={() => onViewPhoto?.(row.photo.url)}
                      style={btnPhoto}
                    >
                      Foto bekijken
                    </button>
                  ) : (
                    <em>-</em>
                  )}
                </td>

                <td style={td}>
                  {row.status}
                  {row.status !== "pending" && row.approvedBy ? (
                    <>
                      <br />
                      <small>by: {row.approvedBy}</small>
                      <br />
                      {row.approvedAt && (
                        <small>{formatDateTime(row.approvedAt)}</small>
                      )}
                      {row.status === "rejected" && row.reason && (
                        <>
                          <br />
                          <small>reden: {row.reason}</small>
                        </>
                      )}
                    </>
                  ) : null}
                </td>

                <td style={td}>
                  {row.status === "pending" ? (
                    <div
                      style={{
                        display: "flex",
                        gap: 8,
                        flexWrap: "wrap",
                        justifyContent: "center",
                      }}
                    >
                      <button
                        onClick={() => onApprove(row)}
                        style={{
                          ...btnApprove,
                          flex: "1 1 140px",
                          minWidth: 120,
                          marginBottom: 12,
                        }}
                      >
                        Goedkeuren
                      </button>

                      <button
                        onClick={() => onReject(row)}
                        style={{
                          ...btnReject,
                          flex: "1 1 140px",
                          minWidth: 120,
                          marginBottom: 12,
                        }}
                      >
                        Afwijzen
                      </button>
                    </div>
                  ) : (
                    <em>-</em>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 8,
            marginTop: 16,
            flexWrap: "wrap",
          }}
        >
          <button
  onClick={() => setPage((p) => Math.max(p - 1, 1))}
  disabled={page === 1}
  style={{
    ...btnPagination,
    opacity: page === 1 ? 0.5 : 1,
  }}
>
  ← Vorige
</button>

          <span>
            Pagina {page} van {totalPages}
          </span>

          <button
  onClick={() =>
    setPage((p) => Math.min(p + 1, totalPages))
  }
  disabled={page === totalPages}
  style={{
    ...btnPagination,
    opacity: page === totalPages ? 0.5 : 1,
  }}
>
  Volgende →
</button>
        </div>
      )}
    </>
  );
};

const TransportWarehouse = () => {
  const [pending, setPending] = useState([]);
  const [approved, setApproved] = useState([]);
  const [rejected, setRejected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);

  // ✅ foto modal state
  const [photoModalUrl, setPhotoModalUrl] = useState(null);

  // ✅ Haal ingelogde transportUser op voor naam
  const transportUser = JSON.parse(localStorage.getItem("transportUser") || "null");
  const approverName = transportUser?.username || transportUser?.company || "Chef Magazijn";

  const fetchByStatus = useCallback(async (status) => {
    const { data } = await axios.get(`${API}/api/fust/pending`, {
      params: status ? { status } : {},
    });
    return data;
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [p, a, r] = await Promise.all([
        fetchByStatus("pending"),
        fetchByStatus("approved"),
        fetchByStatus("rejected"),
      ]);
      setPending(p || []);
      setApproved(a || []);
      setRejected(r || []);
    } catch (e) {
      console.error("❌ Ophalen fustlijsten mislukt:", e);
      setError("Kon fustlijsten niet laden.");
    } finally {
      setLoading(false);
    }
  }, [fetchByStatus]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // ESC sluit modal
  useEffect(() => {
    if (!photoModalUrl) return;
    const onKey = (e) => {
      if (e.key === "Escape") setPhotoModalUrl(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [photoModalUrl]);

  const handleViewPhoto = (url) => {
    if (!url) return;
    setPhotoModalUrl(url);
  };

  const handleApprove = async (row) => {
    if (!window.confirm("Weet je zeker dat je deze melding wil goedkeuren en boeken in BC?")) return;
    setBusyId(row._id);
    try {
      const { data } = await axios.post(`${API}/api/fust/pending/approve/${row._id}`, {
        approvedBy: approverName,
      });
      if (data.ok) {
        setPending((prev) => prev.filter((x) => x._id !== row._id));
        setApproved((prev) => [
          {
            ...row,
            status: "approved",
            approvedBy: approverName,
            approvedAt: new Date().toISOString(),
          },
          ...prev,
        ]);
      } else {
        await loadAll();
        alert("Deels gelukt (207). Controleer details in log.");
      }
    } catch (e) {
      console.error("❌ Goedkeuren mislukt:", e?.response?.data || e.message);
      alert("Goedkeuren mislukt. Probeer opnieuw.");
    } finally {
      setBusyId(null);
    }
  };

  const handleReject = async (row) => {
    const reason = window.prompt("Reden van afwijzing:", "");
    if (reason === null) return;
    setBusyId(row._id);
    try {
      const { data } = await axios.post(`${API}/api/fust/pending/reject/${row._id}`, {
        rejectedBy: approverName,
        reason,
      });
      if (data?.success) {
        setPending((prev) => prev.filter((x) => x._id !== row._id));
        setRejected((prev) => [
          {
            ...row,
            status: "rejected",
            approvedBy: approverName,
            approvedAt: new Date().toISOString(),
            reason,
          },
          ...prev,
        ]);
      } else {
        await loadAll();
      }
    } catch (e) {
      console.error("❌ Afwijzen mislukt:", e?.response?.data || e.message);
      alert("Afwijzen mislukt. Probeer opnieuw.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="transport-warehouse">
      <TransportNavbar />
      <div style={{ padding: 16 }}>
        <h1>📦 Magazijn — Fust Goedkeuren</h1>
        {loading && <p>Laden…</p>}
        {error && <p style={{ color: "crimson" }}>⚠️ {error}</p>}

        <Section title="Te keuren (pending)">
          {busyId && <p style={{ opacity: 0.7 }}>Bezig met verwerken…</p>}
          <List
            items={pending}
            onApprove={handleApprove}
            onReject={handleReject}
            onViewPhoto={handleViewPhoto}
          />
        </Section>

        <Section title="Afgekeurd">
  <List
    items={rejected}
    onViewPhoto={handleViewPhoto}
    pageSize={5}
  />
</Section>

<Section title="Goedgekeurd">
  <List
    items={approved}
    onViewPhoto={handleViewPhoto}
    pageSize={10}
  />
</Section>
      </div>

      {/* ✅ Foto modal */}
      {photoModalUrl && (
        <div onClick={() => setPhotoModalUrl(null)} style={photoOverlay} role="presentation">
          <div
            onClick={(e) => e.stopPropagation()}
            style={photoModal}
            role="dialog"
            aria-modal="true"
          >
            <div style={photoHeader}>
              <strong>Foto</strong>
              <button type="button" onClick={() => setPhotoModalUrl(null)} style={btnClose}>
                Sluiten ✕
              </button>
            </div>

            <img src={photoModalUrl} alt="Fust foto" style={photoImg} />
          </div>
        </div>
      )}
    </div>
  );
};

/* ====== styles ====== */
const th = { border: "1px solid #ddd", textAlign: "left", padding: "8px", background: "#fafafa" };
const td = { border: "1px solid #eee", padding: "8px", verticalAlign: "top" };
const btnApprove = {
  padding: "10px 16px",
  background: "#0b7",
  border: "none",
  color: "white",
  borderRadius: 6,
  cursor: "pointer",
};
const btnReject = {
  padding: "10px 16px",
  background: "#c33",
  border: "none",
  color: "white",
  borderRadius: 6,
  cursor: "pointer",
};
const btnPhoto = {
  padding: "10px 16px",
  background: "orange",
  border: "none",
  color: "white",
  borderRadius: 6,
  cursor: "pointer",
};

const btnPagination = {
  padding: "10px 16px",
  background: "#3c89ba",
  border: "none",
  color: "white",
  borderRadius: 8,
  cursor: "pointer",
  minWidth: 120,
  fontSize: 16,
};

const photoOverlay = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.7)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 16,
  zIndex: 9999,
};

const photoModal = {
  width: "min(1000px, 95vw)",
  maxHeight: "90vh",
  background: "white",
  borderRadius: 10,
  overflow: "hidden",
  boxShadow: "0 10px 30px rgba(0,0,0,0.3)",
  display: "flex",
  flexDirection: "column",
};

const photoHeader = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "10px 12px",
  borderBottom: "1px solid #eee",
};

const btnClose = {
  padding: "6px 10px",
  background: "#111827",
  border: "none",
  color: "white",
  borderRadius: 6,
  cursor: "pointer",
};

const photoImg = {
  width: "100%",
  height: "100%",
  objectFit: "contain",
  background: "#0b0b0b",
  maxHeight: "calc(90vh - 48px)",
};

function formatDateTime(x) {
  if (!x) return "-";
  const d = new Date(x);
  if (Number.isNaN(d.getTime())) return String(x);
  return d.toLocaleString();
}

export default TransportWarehouse;
